import React from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { CommentFetchStatus } from '../../lib/types';
import { getStatusIcon } from '../../lib/statusHelpers';
interface CommentsProgressProps {
  progress: CommentFetchStatus;
}
export function CommentsProgress({
  progress
}: CommentsProgressProps) {
  const getStatusColor = () => {
    switch (progress.status) {
      case 'in_progress':
        return 'text-info';
      case 'completed':
        return 'text-success';
      case 'failed':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };
  // Map CommentsProgress statuses to transcription statuses for the shared helper
  const mappedStatus = progress.status === 'in_progress' ? 'processing' : progress.status === 'completed' ? 'completed' : progress.status === 'failed' ? 'failed' : undefined;
  return <div className="border border-primary/30 rounded-lg bg-card p-4" data-id="element-1175">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-1176">
        <h4 className="font-medium text-sm flex items-center gap-2" data-id="element-1177">
          <span className={getStatusColor()} data-id="element-1178">
            {getStatusIcon(mappedStatus)}
          </span>
          {progress.status === 'in_progress' && 'Fetching Comments...'}
          {progress.status === 'completed' && 'Fetch Complete!'}
          {progress.status === 'failed' && 'Fetch Failed'}
        </h4>
        {progress.provider && <span className="text-xs text-muted-foreground" data-id="element-1179">
            via {progress.provider}
          </span>}
      </div>

      {/* Progress Bar */}
      <div className="mb-4" data-id="element-1180">
        <div className="flex items-center justify-between mb-2" data-id="element-1181">
          <span className="text-xs text-muted-foreground" data-id="element-1182">Progress</span>
          <span className="text-xs font-medium" data-id="element-1183">
            {progress.progress_percent.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-1184">
          <div className={`h-full transition-all duration-500 ${progress.status === 'completed' ? 'bg-success' : progress.status === 'failed' ? 'bg-destructive' : 'bg-info'}`} style={{
          width: `${progress.progress_percent}%`
        }} data-id="element-1185" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1186">
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1187">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1188">Fetched</div>
          <div className="text-lg font-semibold" data-id="element-1189">
            {progress.comments_fetched.toLocaleString()}
          </div>
        </div>
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1190">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1191">Estimated</div>
          <div className="text-lg font-semibold" data-id="element-1192">
            {progress.estimated_total.toLocaleString()}
          </div>
        </div>
        {progress.current_page && progress.total_pages_estimate && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1193">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1194">Page</div>
            <div className="text-lg font-semibold" data-id="element-1195">
              {progress.current_page}/{progress.total_pages_estimate}
            </div>
          </div>}
        {progress.eta_seconds && progress.status === 'in_progress' && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1196">
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1" data-id="element-1197">
              <Clock size={10} data-id="element-1198" />
              ETA
            </div>
            <div className="text-lg font-semibold" data-id="element-1199">{progress.eta_seconds}s</div>
          </div>}
        {progress.credits_charged !== undefined && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1200">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1201">Credits</div>
            <div className="text-lg font-semibold text-primary" data-id="element-1202">
              {progress.credits_charged}
            </div>
          </div>}
      </div>

      {/* Error Message */}
      {progress.status === 'failed' && progress.error_message && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-id="element-1203">
          <div className="flex items-start gap-2" data-id="element-1204">
            <AlertCircle size={16} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-1205" />
            <div className="text-xs text-destructive" data-id="element-1206">
              <p className="font-medium mb-1" data-id="element-1207">Error occurred:</p>
              <p data-id="element-1208">{progress.error_message}</p>
            </div>
          </div>
        </div>}

      {/* Success Message */}
      {progress.status === 'completed' && <div className="p-3 bg-success/10 border border-success/20 rounded-lg" data-id="element-1209">
          <div className="flex items-start gap-2" data-id="element-1210">
            <CheckCircle size={16} className="text-success mt-0.5 flex-shrink-0" data-id="element-1211" />
            <div className="text-xs text-success" data-id="element-1212">
              <p className="font-medium" data-id="element-1213">
                Successfully fetched{' '}
                {progress.comments_fetched.toLocaleString()} comments!
              </p>
              <p className="text-xs mt-1" data-id="element-1214">
                Charged {progress.credits_charged}{' '}
                {progress.credits_charged === 1 ? 'credit' : 'credits'}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}